import { useCallback } from 'react';
import { projectData } from '@/data/project';
import { supabase } from '@/integrations/supabase/client';
import { getStoredUTMParams } from '@/lib/analytics';

const WA_TOKEN_KEY = 'ncv_wa_token';
const WA_TOKEN_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

interface StoredToken {
  token: string;
  timestamp: number;
  source: string;
}

/**
 * Get the stored WhatsApp token (if still valid)
 */
export function getStoredWhatsAppToken(): string | null {
  try {
    const stored = sessionStorage.getItem(WA_TOKEN_KEY);
    if (!stored) return null;
    
    const data: StoredToken = JSON.parse(stored);
    if (Date.now() - data.timestamp > WA_TOKEN_EXPIRY) {
      sessionStorage.removeItem(WA_TOKEN_KEY);
      return null;
    }
    
    return data.token;
  } catch {
    return null;
  }
}

/**
 * Generate a unique token for WhatsApp tracking
 */
function generateToken(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `wa_${timestamp}_${random}`;
}

/**
 * Store a WhatsApp token
 */
function storeToken(token: string, source: string): void {
  try {
    const data: StoredToken = {
      token,
      timestamp: Date.now(),
      source,
    };
    sessionStorage.setItem(WA_TOKEN_KEY, JSON.stringify(data));
  } catch {
    // Session storage might be disabled
  }
}

/**
 * Get message template for source
 */
function getMessageTemplate(source: string): string {
  const templates: Record<string, string> = {
    brochure: `Hi, I'd like the brochure for ${projectData.projectName}.\n\nPlease share the project overview and pricing details.`,
    price: `Hi, I'm interested in pricing for ${projectData.projectName}.\n\nPlease share current prices and availability.`,
    floorplan: `Hi, I'm reviewing floor plans for ${projectData.projectName}.\n\nPlease share detailed layouts and availability.`,
    site_visit: `Hi, I'd like to schedule a site visit for ${projectData.projectName}.\n\nPlease share available timings.`,
    virtual_tour: `Hi, I'd like access to the virtual tour for ${projectData.projectName}.`,
    contact: `Hi, I'm interested in ${projectData.projectName}.\n\nPlease share project details.`,
  };
  
  return templates[source] || templates.contact;
}

/**
 * Hook for WhatsApp lead token tracking
 */
export function useWhatsAppLeadToken() {
  /**
   * Log the WhatsApp click to the server
   */
  const logClick = useCallback(async (token: string, source: string) => {
    try {
      const utm = getStoredUTMParams();
      
      await supabase.functions.invoke('log-whatsapp-click', {
        body: {
          token,
          source,
          page_path: window.location.pathname,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          device_type: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          utm_source: utm.utm_source,
          utm_medium: utm.utm_medium,
          utm_campaign: utm.utm_campaign,
          utm_content: utm.utm_content,
          utm_term: utm.utm_term,
        },
      });
    } catch {
      // Silently fail - don't block WhatsApp opening
    }
  }, []);

  /**
   * Open WhatsApp with tracking
   */
  const openWhatsApp = useCallback(async (options: {
    source: string;
    messageTemplate?: string;
    includeGoldCampaign?: boolean;
  }) => {
    const { source, messageTemplate } = options;
    
    // Generate or reuse token
    let token = getStoredWhatsAppToken();
    if (!token) {
      token = generateToken();
      storeToken(token, source);
    }
    
    // Log click (fire and forget)
    logClick(token, source);
    
    // Get message
    const message = messageTemplate || getMessageTemplate(source);
    const encodedMessage = encodeURIComponent(message);
    
    // Open WhatsApp
    const waUrl = `https://wa.me/${projectData.contact.whatsappNumber}?text=${encodedMessage}`;
    window.open(waUrl, '_blank');
  }, [logClick]);

  return { openWhatsApp, getStoredWhatsAppToken };
}
